import { dashesToCamel, camelToDashes } from './util';

/**
 * Checks if a component is in ec namespace
 * @param {string} component - Name of the component
 * @return {boolean} - True if name starts with ec-, otherwise false
 */
export function isEcComponent(component) {
    return /^ec-/.test(component);
}

/**
 * Returns components that depend on angular
 * (Functionality needs updating to use a better check than is ec)
 * @param {object} components - List of all components
 * @return {array} - Array of angular-dependent components
 */
export function getAngularModuleDependencies(components) {
    const modules = [];

    Object.keys(components).forEach((component) => {
        if (isEcComponent(component)) {
            modules.push(dashesToCamel(component));
        }
    });

    return modules;
}

// BUNDLE HANDLING
/**
 * @param {string} instid - institutionId
 * @param {array} availableVersions - array of available versions
 * @param {function} filterFn - filter function
 */
function getFinalVersion(instid, availableVersions, filterFn) {
    const validVersions = availableVersions.filter(filterFn);
    if (validVersions.length) {
        return {
            version: validVersions[validVersions.length - 1],
            instid,
        };
    }
    return null;
}

/**
 * @param {string} version or version range string
 * @param {string} component name e.g. ec-button
 * @param {object} availableComponents - list of available components
 *     for example:
 *     {
 *         "ec-example": {
 *             "DEFAULT": [
 *                 "1.0.0"
 *             ]
 *         },
 *         "markets-components-example": {
 *             "DEFAULT": [
 *                 "1.0.0"
 *             ]
 *         },
 *         "markets-components-heatmap": {
 *             "DEFAULT": [
 *                 "2.11.0"
 *             ],
 *             "PATS": [
 *                 "2.11.0",
 *                 "2.11.1",
 *                 "2.12.0",
 *                 "3.0.0"
 *             ]
 *         }
 *     }
 * @param {string} instid - institutionId
 */
export function getVersion(version, component, availableComponents, instid) {
    const componentVersions = availableComponents[component];
    if (!componentVersions) {
        // log.warn('Cannot find specified component: %s', component);
        return {};
    }

    let availableVersions = componentVersions[instid];
    if (!availableVersions) {
        // log.info('Cannot find specified component: %s for instid %s. Change to use DEFAULT.', component, instid);
        instid = 'DEFAULT';
        availableVersions = componentVersions[instid];
    }

    let versionObj;
    let temp;
    let ret;

    if (/^~\d+\.\d+\.\d+/.test(version)) {
        // if version is starting with ~, return the latest patch one.
        versionObj = version.substring(1).split('.');
        ret = getFinalVersion(instid, availableVersions, (v) => {
            temp = v.split('.');
            return temp[0] === versionObj[0] && temp[1] === versionObj[1] && temp[2] >= versionObj[2];
        });
    } else if (/^\^\d+\.\d+\.\d+/.test(version)) {
        // if version is starting with ^, return the latest minor one.
        versionObj = version.substring(1).split('.');
        ret = getFinalVersion(instid, availableVersions, (v) => {
            temp = v.split('.');
            return temp[0] === versionObj[0] && temp[1] >= versionObj[1];
        });
    } else if (version === '*') {
        // if version is *, return the latest one.
        ret = {
            version: availableVersions[availableVersions.length - 1],
            instid,
        };
    } else {
        // if version is x.x.x, return the matched one.
        ret = getFinalVersion(instid, availableVersions, v => v === version);
    }

    if (!ret) {
        ret = {};
        // log.warn('Cannot find specified version %s for component: %s with instid: %s', version, component, instid);
        // log.info('Please use available versions [%s] for component: %s with instid: %s', availableVersions.join(','), component, instid);
    }

    return ret;
}

/**
 * Returns components and versions
 * @param {object} loaderConfig object
 * @return {object} list of components and version
 */
export function getBowerDependencies(loaderConfig) {
    const appConfig = loaderConfig.config || {};
    const angularOptions = loaderConfig.angular;
    const validComponents = loaderConfig.validBundles || {};
    const instid = loaderConfig.componentSource && loaderConfig.componentSource.instid ? loaderConfig.componentSource.instid : null;
    let components = appConfig.components || {};
    const dependencies = {};

    if (loaderConfig.componentSource && loaderConfig.componentSource.useClientSpecificBundle) {
        if (Object.keys(components).length === 0) {
            throw new Error('Missing components from appConfig');
        } else {
            const firstKey = Object.keys(components)[0];
            if (!components[firstKey].type) {
                throw new Error('Missing type from first component in appConfig');
            } else {
                // For client specific bundles we leave only one component in the appConfig
                // and use it as the 'main' component for the bundle by overriding its bundle files
                components = {
                    [firstKey]: components[firstKey],
                };
            }
        }
    }

    Object.keys(components).forEach((componentName) => {
        const component = components[componentName];
        // todo - camelToDashes will throw an error if component.type is undefined
        // this makes the check on line 155 redundant so we should clean this up
        const componentNameDash = camelToDashes(component.type);
        let version = null;

        if (loaderConfig.componentSource.type === 'cdn') { // cdn == resolver in old ec-loader
            version = {
                version: component.version,
            };
        } else {
            version = getVersion(component.version, componentNameDash, validComponents, instid);
        }

        if (componentNameDash) {
            if (!window.angular && !dependencies.angular && isEcComponent(componentNameDash)) {
                dependencies.angular = {
                    version: angularOptions.version || '1.5.5',
                };
            }
            dependencies[componentNameDash] = version;
        }
    });

    return dependencies;
}

export default {
    getAngularModuleDependencies,
    getBowerDependencies,
    isEcComponent,
    getVersion,
};



// WEBPACK FOOTER //
// ./src/component-helper.js